import React, { useCallback, useEffect, useState } from 'react';
import Markdown from 'react-markdown';
import { useNavigate, useParams } from 'react-router-dom';
import { styled } from 'styled-components';
import type { ActionsInner, Conversation, Message, OnboardingEventDetail, ParsedSuggestion, SuggestionStats, TraceSummary, UserBasicInfo } from '@dazzle/api-client';
import { DeveloperTier, SuggestionStatus, SuggestionType } from '@dazzle/api-client';

import BlueSpinner from 'Common/components/BlueSpinner';
import {
  conversationGetConversationsInternal,
  conversationGetInternal,
  mcpTraceGetInternal,
  mcpTraceListInternal,
  onboardingGetEventsInternal,
  suggestionGetDedupedInternal,
  traceGetInternal,
  traceListInternal,
  userGetAllInternal,
  userSetDeveloperTierInternal
} from 'Common/api/DazzleInternalService';
import { sanitizeUrl } from 'Common/util/Links';
import Modal from 'Common/components/Modal';
import { useModalContext } from 'Common/contexts/ModalContext';

import CompassHeader, { PageType } from 'Compass/components/CompassHeader';
import { Button } from 'Compass/components/styled/Button';
import TraceDetailModal from 'Compass/components/TraceDetailModal';
import { formatDateShort } from 'Compass/utils/orderUtils';

const TRACE_STATUS_LABELS: Record<number, string> = { 1: 'Pending', 2: 'Completed', 3: 'Error' };

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)} sec`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins < 60) return `${mins}:${String(secs).padStart(2, '0')} min`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  return `${hrs}:${String(remMins).padStart(2, '0')} hr`;
};

const UserViewPage = () => {
  const navigate = useNavigate();
  const { userLid } = useParams<{ userLid?: string }>();

  const [users, setUsers] = useState<UserBasicInfo[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserBasicInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingUserData, setLoadingUserData] = useState(false);
  const [suggestions, setSuggestions] = useState<ParsedSuggestion[]>([]);
  const [suggestionStats, setSuggestionStats] = useState<SuggestionStats | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [suggestionsExpanded, setSuggestionsExpanded] = useState(false);
  const [conversationsExpanded, setConversationsExpanded] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [pageToken, setPageToken] = useState<string | undefined>(undefined);
  const [suggestionsHasMore, setSuggestionsHasMore] = useState(false);
  const [suggestionsPageToken, setSuggestionsPageToken] = useState<string | undefined>(undefined);
  const [loadingMoreSuggestions, setLoadingMoreSuggestions] = useState(false);
  const [traceSummaries, setTraceSummaries] = useState<Map<string, TraceSummary[]>>(new Map());
  const [loadingTrace, setLoadingTrace] = useState<string | null>(null);
  const [expandedConversations, setExpandedConversations] = useState<Set<string>>(new Set());
  const [conversationMessages, setConversationMessages] = useState<Map<string, Message[]>>(new Map());
  const [loadingConversations, setLoadingConversations] = useState<Set<string>>(new Set());
  const [onboardingEvents, setOnboardingEvents] = useState<OnboardingEventDetail[]>([]);
  const [onboardingExpanded, setOnboardingExpanded] = useState(false);
  const [developerTier, setDeveloperTier] = useState<string | null>(null);
  const [tierSaving, setTierSaving] = useState(false);
  const [mcpTracesExpanded, setMcpTracesExpanded] = useState(false);
  const [mcpTraces, setMcpTraces] = useState<TraceSummary[]>([]);
  const [mcpTracesPageToken, setMcpTracesPageToken] = useState<string | undefined>(undefined);
  const [mcpTracesLoading, setMcpTracesLoading] = useState(false);
  const [mcpTracesLoaded, setMcpTracesLoaded] = useState(false);
  const [loadingMcpTrace, setLoadingMcpTrace] = useState<string | null>(null);

  const modalContext = useModalContext();

  useEffect(() => {
    void fetchUsers();
  }, []);

  const fetchUsers = async (token?: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await userGetAllInternal({ limit: 100, pageToken: token, sort_by: 2, sort_order: 2 });
      if (response) {
        if (token) {
          setUsers((prev) => [...prev, ...response.users]);
        } else {
          setUsers(response.users);
        }
        setHasMore(response.has_more);
        setPageToken(response.page_token);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error fetching users');
    } finally {
      setLoading(false);
    }
  };

  const loadUserData = useCallback(async (user: UserBasicInfo) => {
    setSelectedUser(user);
    setLoadingUserData(true);
    setSuggestions([]);
    setSuggestionStats(null);
    setConversations([]);
    setSuggestionsExpanded(false);
    setConversationsExpanded(false);
    setOnboardingEvents([]);
    setOnboardingExpanded(false);
    setDeveloperTier(user.developer_tier ?? null);
    setSuggestionsHasMore(false);
    setSuggestionsPageToken(undefined);
    setTraceSummaries(new Map());
    setExpandedConversations(new Set());
    setConversationMessages(new Map());
    setMcpTracesExpanded(false);
    setMcpTraces([]);
    setMcpTracesPageToken(undefined);
    setMcpTracesLoaded(false);

    try {
      const [suggestionsRes, conversationsRes, onboardingRes] = await Promise.all([
        suggestionGetDedupedInternal({ user_lid: user.user_lid, limit: 100 }),
        conversationGetConversationsInternal({ user_lid: user.user_lid, limit: 20 }),
        onboardingGetEventsInternal({ user_lid: user.user_lid }),
      ]);

      if (suggestionsRes) {
        setSuggestions(suggestionsRes.suggestions);
        setSuggestionStats(suggestionsRes.stats ?? null);
        setSuggestionsHasMore(suggestionsRes.has_more);
        setSuggestionsPageToken(suggestionsRes.page_token);
      }
      if (onboardingRes) {
        setOnboardingEvents(onboardingRes.events);
      }
      if (conversationsRes) {
        const sorted = [...conversationsRes.conversations].sort((a, b) => {
          const aLastMsg = a.messages?.[a.messages.length - 1];
          const bLastMsg = b.messages?.[b.messages.length - 1];
          const aTs = aLastMsg?.message_ts ? new Date(aLastMsg.message_ts).getTime() : 0;
          const bTs = bLastMsg?.message_ts ? new Date(bLastMsg.message_ts).getTime() : 0;
          return bTs - aTs;
        });
        setConversations(sorted);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error fetching user data');
    } finally {
      setLoadingUserData(false);
    }
  }, []);

  const handleUserClick = (user: UserBasicInfo) => {
    navigate(`/user-view/${user.user_lid}`);
  };

  const handleToggleDeveloperTier = async () => {
    if (!selectedUser || tierSaving) return;
    const next: DeveloperTier | null = developerTier === null ? DeveloperTier.Beta : null;
    setTierSaving(true);
    try {
      const response = await userSetDeveloperTierInternal(selectedUser.user_lid, next);
      const newTier = response?.tier ?? null;
      setDeveloperTier(newTier);
      setUsers(prev => prev.map(u =>
        u.user_lid === selectedUser.user_lid ? { ...u, developer_tier: newTier ?? undefined } : u
      ));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error updating developer tier');
    } finally {
      setTierSaving(false);
    }
  };

  const fetchTracesForConversation = async (conversationId: string, userLid: number) => {
    if (traceSummaries.has(conversationId)) return;

    try {
      const res = await traceListInternal({ user_lid: userLid, conversation_id: conversationId });
      if (res) {
        setTraceSummaries((prev) => new Map(prev).set(conversationId, res.traces));
      }
    } catch (e) {
      console.error('Error fetching traces:', e);
    }
  };

  const handleViewTrace = async (conversationId: string, userLid: number, messageId: string) => {
    setLoadingTrace(messageId);
    try {
      const res = await traceGetInternal({ user_lid: userLid, conversation_id: conversationId, message_id: messageId });
      if (res?.trace) {
        modalContext.show({
          contents: (
            <Modal onClickOutside={modalContext.hide} maxWidth="1000px">
              <TraceDetailModal trace={res.trace} onClose={modalContext.hide} />
            </Modal>
          ),
        });
      }
    } catch (e) {
      console.error('Error fetching trace:', e);
      setError(e instanceof Error ? e.message : 'Error fetching trace');
    } finally {
      setLoadingTrace(null);
    }
  };

  const fetchMcpTraces = async (userLid: number, token?: string) => {
    setMcpTracesLoading(true);
    try {
      const res = await mcpTraceListInternal({ user_lid: userLid, page_token: token });
      if (res) {
        setMcpTraces((prev) => (token ? [...prev, ...res.traces] : res.traces));
        setMcpTracesPageToken(res.next_page_token ?? undefined);
        setMcpTracesLoaded(true);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error fetching MCP traces');
    } finally {
      setMcpTracesLoading(false);
    }
  };

  const toggleMcpTraces = () => {
    const next = !mcpTracesExpanded;
    setMcpTracesExpanded(next);
    if (next && !mcpTracesLoaded && selectedUser) {
      void fetchMcpTraces(selectedUser.user_lid);
    }
  };

  const handleViewMcpTrace = async (userLid: number, traceId: string) => {
    setLoadingMcpTrace(traceId);
    try {
      const res = await mcpTraceGetInternal({ user_lid: userLid, trace_id: traceId });
      if (res?.trace) {
        modalContext.show({
          contents: (
            <Modal onClickOutside={modalContext.hide} maxWidth="1000px">
              <TraceDetailModal trace={res.trace} onClose={modalContext.hide} />
            </Modal>
          ),
        });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error fetching MCP trace');
    } finally {
      setLoadingMcpTrace(null);
    }
  };

  const getTraceSummaryForMessage = (conversationId: string, messageId: string): TraceSummary | undefined => {
    const conversationTraces = traceSummaries.get(conversationId);
    if (!conversationTraces) return undefined;
    return conversationTraces.find((t) => t.message_id === messageId);
  };

  const getOrphanedTraces = (conversationId: string, messages: Message[]): TraceSummary[] => {
    const conversationTraces = traceSummaries.get(conversationId);
    if (!conversationTraces) return [];
    const messageIds = new Set(messages.map((m) => m.id).filter((id): id is string => id !== undefined));
    return conversationTraces.filter((t) => !messageIds.has(t.message_id!));
  };

  const loadMoreSuggestions = async () => {
    if (!selectedUser || !suggestionsPageToken || loadingMoreSuggestions) return;

    setLoadingMoreSuggestions(true);
    try {
      const res = await suggestionGetDedupedInternal({
        user_lid: selectedUser.user_lid,
        limit: 100,
        page_token: suggestionsPageToken,
      });

      if (res) {
        setSuggestions((prev) => [...prev, ...res.suggestions]);
        setSuggestionsHasMore(res.has_more);
        setSuggestionsPageToken(res.page_token);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error loading more suggestions');
    } finally {
      setLoadingMoreSuggestions(false);
    }
  };

  const handleBack = () => {
    navigate('/user-view');
  };

  const toggleConversation = async (conversationId: string) => {
    if (expandedConversations.has(conversationId)) {
      setExpandedConversations((prev) => {
        const next = new Set(prev);
        next.delete(conversationId);
        return next;
      });
    } else {
      setExpandedConversations((prev) => new Set(prev).add(conversationId));

      if (!conversationMessages.has(conversationId)) {
        setLoadingConversations((prev) => new Set(prev).add(conversationId));
        try {
          const response = await conversationGetInternal(conversationId);
          if (response?.conversation?.messages) {
            setConversationMessages((prev) => new Map(prev).set(conversationId, response.conversation.messages));
          }
        } catch (e) {
          setError(e instanceof Error ? e.message : 'Error loading conversation messages');
        } finally {
          setLoadingConversations((prev) => {
            const next = new Set(prev);
            next.delete(conversationId);
            return next;
          });
        }
      }
    }
  };

  useEffect(() => {
    if (userLid && users.length > 0) {
      const userLidNum = parseInt(userLid, 10);
      const user = users.find((u) => u.user_lid === userLidNum);
      if (user && user.user_lid !== selectedUser?.user_lid) {
        void loadUserData(user);
      }
    } else if (!userLid) {
      setSelectedUser(null);
      setSuggestions([]);
      setConversations([]);
    }
  }, [userLid, users, loadUserData, selectedUser?.user_lid]);

  const loadMore = () => {
    if (hasMore && pageToken) {
      void fetchUsers(pageToken);
    }
  };

  if (selectedUser) {
    return (
      <PageContainer>
        <CompassHeader pageType={PageType.UserView} />
        <Content>
          <BackButton onClick={handleBack}>← Back to Users</BackButton>
          <UserHeader>
            <UserName>{selectedUser.name}</UserName>
            <UserDetails>
              <UserDetail>LID: {selectedUser.user_lid}</UserDetail>
              <UserDetail>{selectedUser.phone_number}</UserDetail>
              {selectedUser.email && <UserDetail>{selectedUser.email}</UserDetail>}
            </UserDetails>
            <DeveloperTierRow>
              <DeveloperTierLabel>Developer Tier</DeveloperTierLabel>
              <DeveloperTierToggle
                onClick={handleToggleDeveloperTier}
                disabled={tierSaving}
                $active={developerTier !== null}
              >
                {developerTier === null ? 'OFF' : developerTier}
              </DeveloperTierToggle>
            </DeveloperTierRow>
          </UserHeader>

          {loadingUserData && <BlueSpinner />}

          {!loadingUserData && onboardingEvents.length > 0 && (
            <Section>
              <SectionHeader onClick={() => setOnboardingExpanded(!onboardingExpanded)}>
                <SectionHeaderLeft>
                  <h3>Onboarding Events</h3>
                  <SectionStats>{onboardingEvents.length} events</SectionStats>
                </SectionHeaderLeft>
                <ExpandIcon>{onboardingExpanded ? '▼' : '▶'}</ExpandIcon>
              </SectionHeader>
              {onboardingExpanded && (
                <OnboardingTable>
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Timestamp</th>
                      <th>Step Latency</th>
                      <th>Overall Latency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {onboardingEvents.map((evt) => (
                      <tr key={evt.event_name}>
                        <td>{evt.event_name}</td>
                        <td>{new Date(evt.event_ts).toLocaleString()}</td>
                        <td>{evt.step_latency_seconds != null ? formatDuration(evt.step_latency_seconds) : '—'}</td>
                        <td>
                          {evt.overall_latency_seconds != null ? (
                            <OverallLatencyCell>
                              <span>{formatDuration(evt.overall_latency_seconds)}</span>
                              <OverallLatencyHint>(from {evt.overall_latency_reference})</OverallLatencyHint>
                            </OverallLatencyCell>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </OnboardingTable>
              )}
            </Section>
          )}

          {!loadingUserData && suggestionStats && (
            <StatsSection>
              <StatsHeader>
                <h3>Summary</h3>
              </StatsHeader>
              <StatsGrid>
                <StatGroup>
                  <StatGroupTitle>By Status</StatGroupTitle>
                  <StatsList>
                    {Object.entries(suggestionStats.status_counts)
                      .sort((a, b) => b[1] - a[1])
                      .map(([status, count]) => (
                        <StatItem key={status}>
                          <StatLabel>{status}</StatLabel>
                          <StatValue>{count}</StatValue>
                        </StatItem>
                      ))}
                  </StatsList>
                </StatGroup>
                <StatGroup>
                  <StatGroupTitle>By Type</StatGroupTitle>
                  <StatsList>
                    {suggestionStats.type_stats.map((typeStat) => (
                      <StatItem key={typeStat.suggestion_type}>
                        <StatLabel>{typeStat.suggestion_type}</StatLabel>
                        <StatValue>
                          {typeStat.total}
                          {typeStat.interacted > 0 && <InteractedBadge>{typeStat.interacted} chatted</InteractedBadge>}
                        </StatValue>
                      </StatItem>
                    ))}
                  </StatsList>
                </StatGroup>
              </StatsGrid>
            </StatsSection>
          )}

          {!loadingUserData && suggestions.length > 0 && (
            <Section>
              <SectionHeader onClick={() => setSuggestionsExpanded(!suggestionsExpanded)}>
                <SectionHeaderLeft>
                  <h3>Suggestions</h3>
                  <SectionStats>
                    {suggestions.length} total
                    {!suggestionsExpanded &&
                      ` • ${suggestions.filter((s) => s.suggestion.status === SuggestionStatus.Pending).length} pending`}
                  </SectionStats>
                </SectionHeaderLeft>
                <ExpandIcon>{suggestionsExpanded ? '▼' : '▶'}</ExpandIcon>
              </SectionHeader>
              {suggestionsExpanded && (
                <>
                  <SuggestionsList>
                    {suggestions.map((s) => {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      const content = s.suggestion.content as any;
                      const imageUrl = content?.external_image_url || s.photo_url || null;
                      const isIdea = s.suggestion.suggestion_type === SuggestionType.Ideas;
                      return (
                        <SuggestionCard
                          key={s.suggestion.suggestion_id}
                          onClick={() => {
                            modalContext.show({
                              contents: (
                                <Modal onClickOutside={modalContext.hide} maxWidth="800px">
                                  <SuggestionDetailModal parsedSuggestion={s} onClose={modalContext.hide} />
                                </Modal>
                              ),
                            });
                          }}
                        >
                          {imageUrl && <SuggestionCardImage src={imageUrl} alt="" />}
                          <SuggestionCardBody>
                            <SuggestionCardTopRow>
                              <SuggestionTypeBadge>{s.suggestion.suggestion_type}</SuggestionTypeBadge>
                              <SuggestionStatusBadge $status={s.suggestion.status || ''}>
                                {s.suggestion.status}
                              </SuggestionStatusBadge>
                            </SuggestionCardTopRow>
                            <SuggestionCardTitle>{s.suggestion.title || 'Untitled'}</SuggestionCardTitle>
                            {content?.message && <SuggestionCardMessage>{content.message}</SuggestionCardMessage>}
                            {isIdea && content && (
                              <SuggestionCardMeta>
                                {[content.event_location, content.event_date, content.event_time]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </SuggestionCardMeta>
                            )}
                            {!isIdea && content?.type === SuggestionType.Product && content.event?.price != null && (
                              <SuggestionCardMeta>
                                {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                                  content.event.price
                                )}
                              </SuggestionCardMeta>
                            )}
                            {s.suggestion.actions && s.suggestion.actions.length > 0 && (
                              <ActionsList actions={s.suggestion.actions} />
                            )}
                          </SuggestionCardBody>
                        </SuggestionCard>
                      );
                    })}
                  </SuggestionsList>
                  {suggestionsHasMore && (
                    <LoadMoreContainer>
                      <Button onClick={loadMoreSuggestions} disabled={loadingMoreSuggestions}>
                        {loadingMoreSuggestions ? 'Loading...' : 'Load More Suggestions'}
                      </Button>
                    </LoadMoreContainer>
                  )}
                </>
              )}
            </Section>
          )}

          {!loadingUserData && conversations.length > 0 && (
            <Section>
              <SectionHeader onClick={() => setConversationsExpanded(!conversationsExpanded)}>
                <SectionHeaderLeft>
                  <h3>Conversations</h3>
                  <SectionStats>{conversations.length} conversations</SectionStats>
                </SectionHeaderLeft>
                <ExpandIcon>{conversationsExpanded ? '▼' : '▶'}</ExpandIcon>
              </SectionHeader>
              {conversationsExpanded && (
                <ConversationsList>
                  {conversations.map((conv) => {
                    const userLid = selectedUser?.user_lid;
                    const isExpanded = expandedConversations.has(conv.conversation_id);
                    const isLoading = loadingConversations.has(conv.conversation_id);
                    const messages = conversationMessages.get(conv.conversation_id) || [];
                    const orphanedTraces = getOrphanedTraces(conv.conversation_id, messages);

                    if (isExpanded && userLid && !traceSummaries.has(conv.conversation_id)) {
                      void fetchTracesForConversation(conv.conversation_id, userLid);
                    }

                    return (
                      <ConversationItem key={conv.conversation_id}>
                        <ConversationHeaderClickable
                          onClick={() => {
                            if (window.getSelection()?.toString()) return;
                            void toggleConversation(conv.conversation_id);
                          }}
                        >
                          <ConversationHeaderContent>
                            <ConversationTitle>{conv.title || 'Untitled Conversation'}</ConversationTitle>
                            <ConversationId>ID: {conv.conversation_id}</ConversationId>
                          </ConversationHeaderContent>
                          <ConversationHeaderRight>
                            <ExpandIcon>{isExpanded ? '▼' : '▶'}</ExpandIcon>
                          </ConversationHeaderRight>
                        </ConversationHeaderClickable>
                        {isExpanded && (
                          <>
                            {isLoading && (
                              <LoadingMessages>
                                <BlueSpinner />
                              </LoadingMessages>
                            )}
                            {!isLoading && orphanedTraces.length > 0 && (
                              <FailedRequestsSection>
                                <FailedRequestsHeader>Failed Requests</FailedRequestsHeader>
                                {orphanedTraces.map((trace) => {
                                  const isLoadingThisTrace = loadingTrace === trace.message_id;
                                  return (
                                    <FailedRequestItem key={trace.message_id}>
                                      <FailedRequestInfo>
                                        <FailedRequestTime>
                                          {new Date(trace.start_ts).toLocaleString()}
                                        </FailedRequestTime>
                                        <FailedRequestStatus $status={trace.status}>
                                          {TRACE_STATUS_LABELS[trace.status] || 'Unknown'}
                                        </FailedRequestStatus>
                                      </FailedRequestInfo>
                                      {userLid && (
                                        <TraceButton
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            void handleViewTrace(conv.conversation_id, userLid, trace.message_id!);
                                          }}
                                          disabled={isLoadingThisTrace}
                                        >
                                          {isLoadingThisTrace ? 'Loading...' : 'View Trace'}
                                        </TraceButton>
                                      )}
                                    </FailedRequestItem>
                                  );
                                })}
                              </FailedRequestsSection>
                            )}
                            {!isLoading && messages.length > 0 && (
                              <MessagesList>
                                {messages.map((msg, idx) => {
                                  const traceSummary = msg.id
                                    ? getTraceSummaryForMessage(conv.conversation_id, msg.id)
                                    : undefined;
                                  const isLoadingThisTrace = loadingTrace === msg.id;
                                  return (
                                    <MessageItem key={idx}>
                                      <MessageHeaderRow>
                                        <MessageRole>{msg.is_user ? 'USER' : 'ASSISTANT'}</MessageRole>
                                        {!msg.is_user && traceSummary && userLid && msg.id && (
                                          <TraceButton
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              void handleViewTrace(conv.conversation_id, userLid, msg.id!);
                                            }}
                                            disabled={isLoadingThisTrace}
                                          >
                                            {isLoadingThisTrace ? 'Loading...' : 'View Trace'}
                                          </TraceButton>
                                        )}
                                      </MessageHeaderRow>
                                      <MessageContent>
                                        <Markdown>{msg.content}</Markdown>
                                      </MessageContent>
                                      {msg.image_urls && msg.image_urls.length > 0 && (
                                        <MessageImages>
                                          {msg.image_urls.map((url, imgIdx) => (
                                            <MessageImage key={imgIdx} src={url} alt={`Message image ${imgIdx + 1}`} />
                                          ))}
                                        </MessageImages>
                                      )}
                                      {msg.actions && msg.actions.length > 0 && <ActionsList actions={msg.actions} />}
                                    </MessageItem>
                                  );
                                })}
                              </MessagesList>
                            )}
                            {!isLoading && messages.length === 0 && orphanedTraces.length === 0 && (
                              <EmptyMessages>No messages in this conversation</EmptyMessages>
                            )}
                          </>
                        )}
                      </ConversationItem>
                    );
                  })}
                </ConversationsList>
              )}
            </Section>
          )}

          {!loadingUserData && selectedUser && (
            <Section>
              <SectionHeader onClick={toggleMcpTraces}>
                <SectionHeaderLeft>
                  <h3>MCP Traces</h3>
                  <SectionStats>
                    {mcpTracesLoaded
                      ? `${mcpTraces.length} loaded${mcpTracesPageToken ? '+' : ''}`
                      : 'Click to load'}
                  </SectionStats>
                </SectionHeaderLeft>
                <ExpandIcon>{mcpTracesExpanded ? '▼' : '▶'}</ExpandIcon>
              </SectionHeader>
              {mcpTracesExpanded && (
                <>
                  {mcpTracesLoading && mcpTraces.length === 0 && (
                    <LoadingMessages>
                      <BlueSpinner />
                    </LoadingMessages>
                  )}
                  {mcpTracesLoaded && mcpTraces.length === 0 && !mcpTracesLoading && (
                    <EmptyMessages>No MCP traces for this user.</EmptyMessages>
                  )}
                  {mcpTraces.length > 0 && (
                    <McpTracesTable>
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>Status</th>
                          <th>Duration</th>
                          <th>Model</th>
                          <th>LLM</th>
                          <th>Tools</th>
                          <th>Tokens</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {mcpTraces.map((t) => {
                          const traceId = t.trace_id ?? '';
                          const isLoadingThis = loadingMcpTrace === traceId;
                          return (
                            <tr key={traceId}>
                              <td>{new Date(t.start_ts).toLocaleString()}</td>
                              <td>
                                <FailedRequestStatus $status={t.status}>
                                  {TRACE_STATUS_LABELS[t.status] || 'Unknown'}
                                </FailedRequestStatus>
                              </td>
                              <td>
                                {t.total_duration_ms != null ? `${(t.total_duration_ms / 1000).toFixed(2)}s` : '—'}
                              </td>
                              <td>{t.model_name ?? '—'}</td>
                              <td>{t.llm_call_count}</td>
                              <td>{t.tool_call_count}</td>
                              <td>{t.total_tokens?.toLocaleString() ?? '0'}</td>
                              <td>
                                <TraceButton
                                  onClick={() => void handleViewMcpTrace(selectedUser.user_lid, traceId)}
                                  disabled={isLoadingThis || !traceId}
                                >
                                  {isLoadingThis ? 'Loading...' : 'View Trace'}
                                </TraceButton>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </McpTracesTable>
                  )}
                  {mcpTracesPageToken && (
                    <LoadMoreContainer>
                      <Button
                        onClick={() => void fetchMcpTraces(selectedUser.user_lid, mcpTracesPageToken)}
                        disabled={mcpTracesLoading}
                      >
                        {mcpTracesLoading ? 'Loading...' : 'Load More Traces'}
                      </Button>
                    </LoadMoreContainer>
                  )}
                </>
              )}
            </Section>
          )}

          {!loadingUserData && suggestions.length === 0 && conversations.length === 0 && (
            <EmptyState>No suggestions or conversations found for this user.</EmptyState>
          )}
        </Content>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <CompassHeader pageType={PageType.UserView} />
      <Content>
        <Title>User View</Title>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        {loading && users.length === 0 && <BlueSpinner />}

        {users.length > 0 && (
          <CardsGrid>
            {users.map((user) => (
              <UserCard key={user.user_lid} onClick={() => handleUserClick(user)}>
                <CardName>{user.name}</CardName>
                <CardDetails>
                  <CardDetail>LID: {user.user_lid}</CardDetail>
                  <CardDetail>{user.phone_number}</CardDetail>
                  {user.email && <CardDetail>{user.email}</CardDetail>}
                  {user.latest_message_ts && (
                    <CardDetail>Last active: {formatDateShort(new Date(user.latest_message_ts).getTime())}</CardDetail>
                  )}
                </CardDetails>
              </UserCard>
            ))}
          </CardsGrid>
        )}

        {hasMore && (
          <LoadMoreContainer>
            <Button onClick={loadMore} disabled={loading}>
              {loading ? 'Loading...' : 'Load More'}
            </Button>
          </LoadMoreContainer>
        )}

        {!loading && users.length === 0 && !error && <EmptyState>No users found.</EmptyState>}
      </Content>
    </PageContainer>
  );
};

const ACTION_TYPE_CONFIG: Record<string, { label: string; color: string }> = {
  link: { label: 'Link', color: '#2563eb' },
  order: { label: 'Order', color: '#d97706' },
  chat: { label: 'Chat', color: '#16a34a' },
  payment: { label: 'Payment', color: '#db2777' },
  calendar: { label: 'Calendar', color: '#7c3aed' },
  reminder: { label: 'Reminder', color: '#0891b2' },
  contact: { label: 'Contact', color: '#4f46e5' },
  share_photos: { label: 'Share Photos', color: '#0d9488' },
  transcription: { label: 'Transcription', color: '#6366f1' },
  dismiss: { label: 'Dismiss', color: '#9ca3af' },
  idea: { label: 'Idea', color: '#f59e0b' },
  wallet_pass: { label: 'Wallet Pass', color: '#1d4ed8' },
  face_tagging: { label: 'Face Tag', color: '#ec4899' },
};

const getActionDetail = (action: ActionsInner): { primary: string; secondary?: string; href?: string } => {
  switch (action.action_type) {
    case 'link':
      return { primary: action.label, secondary: action.url, href: sanitizeUrl(action.url) };
    case 'order':
      return {
        primary: action.button_label,
        secondary: [action.order_category, action.description].filter(Boolean).join(' — '),
      };
    case 'chat':
      return { primary: action.button_label, secondary: action.message };
    case 'payment':
      return { primary: action.button_label, secondary: action.description };
    case 'calendar':
      return { primary: action.title, secondary: action.location ?? undefined };
    case 'reminder':
      return { primary: action.title, secondary: action.notes ?? undefined };
    case 'contact':
      return { primary: action.name, secondary: [action.phone, action.email].filter(Boolean).join(' · ') };
    case 'share_photos':
      return { primary: action.button_label, secondary: `${action.photo_ids.length} photos` };
    case 'transcription':
      return { primary: action.title };
    default:
      return { primary: 'button_label' in action ? String(action.button_label) : action.action_type };
  }
};

const ActionsList = ({ actions }: { actions: ActionsInner[] }) => (
  <ActionsContainer>
    {actions.map((action, i) => {
      const config = ACTION_TYPE_CONFIG[action.action_type] ?? { label: action.action_type, color: '#6b7280' };
      const detail = getActionDetail(action);
      return (
        <ActionCard key={i}>
          <ActionBadge $color={config.color}>{config.label}</ActionBadge>
          <ActionBody>
            {detail.href ? (
              <ActionPrimary as="a" href={detail.href} target="_blank" rel="noopener noreferrer">
                {detail.primary}
              </ActionPrimary>
            ) : (
              <ActionPrimary>{detail.primary}</ActionPrimary>
            )}
            {detail.secondary && <ActionSecondary>{detail.secondary}</ActionSecondary>}
          </ActionBody>
        </ActionCard>
      );
    })}
  </ActionsContainer>
);

const SuggestionDetailModal = ({
  parsedSuggestion,
  onClose,
}: {
  parsedSuggestion: ParsedSuggestion;
  onClose: () => void;
}) => {
  const { suggestion, photo_url } = parsedSuggestion;
  if (!suggestion.content) {
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const c = suggestion.content as any;
  const isIdea = suggestion.suggestion_type === SuggestionType.Ideas;
  const imageUrl = c.external_image_url || photo_url || null;

  let price: string | null = null;
  let urls: string[] = [];

  if (c.type === SuggestionType.Product && c.event) {
    if (c.event.price != null) {
      price = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(c.event.price);
    }
    urls = (c.event.urls || []).map(sanitizeUrl);
  }

  return (
    <ModalContent>
      <ModalHeader>
        <OrderTitle>{suggestion.title || 'Untitled Suggestion'}</OrderTitle>
        <ModalHeaderRight>
          <SuggestionTypeBadge>{suggestion.suggestion_type}</SuggestionTypeBadge>
          <SuggestionStatusBadge $status={suggestion.status || ''}>{suggestion.status}</SuggestionStatusBadge>
        </ModalHeaderRight>
      </ModalHeader>

      <TwoColumn>
        <DetailSection>
          <DataTable>
            {c.message && (
              <Row>
                <Label>Message</Label>
                <Value>{c.message}</Value>
              </Row>
            )}
            {isIdea && c.description && (
              <Row>
                <Label>Description</Label>
                <Value>{c.description}</Value>
              </Row>
            )}
            {isIdea && c.follow_up && (
              <Row>
                <Label>Follow-up</Label>
                <Value>{c.follow_up}</Value>
              </Row>
            )}
            {isIdea && c.event_date && (
              <Row>
                <Label>Date</Label>
                <Value>{[c.event_date, c.event_time].filter(Boolean).join(' at ')}</Value>
              </Row>
            )}
            {isIdea && c.event_location && (
              <Row>
                <Label>Location</Label>
                <Value>{c.event_location}</Value>
              </Row>
            )}
            {isIdea && c.url && (
              <Row>
                <Label>URL</Label>
                <Value>
                  <a href={sanitizeUrl(c.url)} target="_blank" rel="noopener noreferrer">
                    {c.button_text || c.url}
                  </a>
                </Value>
              </Row>
            )}
            {isIdea && c.tag && (
              <Row>
                <Label>Tag</Label>
                <Value>{c.tag}</Value>
              </Row>
            )}
            {isIdea && c.confidence != null && (
              <Row>
                <Label>Confidence</Label>
                <Value>{Math.round(c.confidence * 100)}%</Value>
              </Row>
            )}
            {price && (
              <Row>
                <Label>Est. Price</Label>
                <Value>{price}</Value>
              </Row>
            )}
            {urls.length > 0 && (
              <Row>
                <Label>URLs</Label>
                <Value>
                  <Urls>
                    {urls.map((url: string, i: number) => (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                        {url}
                      </a>
                    ))}
                  </Urls>
                </Value>
              </Row>
            )}
            <Row>
              <Label>Created</Label>
              <Value>{suggestion.sort_key ? formatDateShort((suggestion.sort_key || 0) * 1000) : 'Unknown'}</Value>
            </Row>
            <Row>
              <Label>Suggestion ID</Label>
              <Value style={{ fontFamily: 'monospace', fontSize: 12 }}>{suggestion.suggestion_id}</Value>
            </Row>
          </DataTable>
        </DetailSection>

        {imageUrl && (
          <ProductImageContainer>
            <ProductImage src={imageUrl} alt="" />
          </ProductImageContainer>
        )}
      </TwoColumn>

      {suggestion.actions && suggestion.actions.length > 0 && (
        <ModalActionsSection>
          <SectionTitle>Actions</SectionTitle>
          <ActionsList actions={suggestion.actions} />
        </ModalActionsSection>
      )}

      <div
        style={{ padding: '24px 32px', borderTop: '1px solid #E4E7EF', display: 'flex', justifyContent: 'flex-end' }}
      >
        <Button onClick={onClose}>Close</Button>
      </div>
    </ModalContent>
  );
};

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 24px 32px;
  background: linear-gradient(to bottom, #fafafc, #ffffff);
  border-bottom: 1px solid #e4e7ef;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const OrderTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #1a1d2b;
  margin: 0;
`;

const ModalHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const TwoColumn = styled.div`
  display: flex;
  gap: 10px;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
  }
`;

const DetailSection = styled.div`
  padding: 24px 32px;
  flex: 1;
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #5a6178;
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DataTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #f0f1f6;

  &:last-child {
    border-bottom: none;
  }
`;

const Label = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #5a6178;
  display: flex;
  align-items: center;
`;

const Value = styled.div`
  font-size: 14px;
  color: #1a1d2b;
  display: flex;
  align-items: center;
  word-break: break-word;
`;

const ProductImageContainer = styled.div`
  padding: 24px 32px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  max-width: 400px;
`;

const ProductImage = styled.img`
  max-height: 400px;
  max-width: 100%;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
`;

const Urls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const PageContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  background-color: #f8f9fc;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 40px auto;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  color: #1a1d2b;
  margin: 0;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #5a6178;
  font-size: 14px;
  cursor: pointer;
  padding: 8px 0;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    color: #1a1d2b;
  }
`;

const UserHeader = styled.div`
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
`;

const UserName = styled.h2`
  margin: 0 0 12px 0;
  font-size: 24px;
  color: #1a1d2b;
`;

const UserDetails = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
`;

const UserDetail = styled.span`
  font-size: 14px;
  color: #8890a5;
`;

const DeveloperTierRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
`;

const DeveloperTierLabel = styled.span`
  font-size: 13px;
  color: #5A6178;
  font-weight: 600;
`;

const DeveloperTierToggle = styled.button<{ disabled?: boolean; $active: boolean }>`
  background: ${props => props.disabled ? '#8890A5' : props.$active ? '#008AFF' : '#5A6178'};
  color: white;
  border: none;
  padding: 6px 14px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.7 : 1};
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
`;

const UserCard = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const CardName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1a1d2b;
  margin-bottom: 8px;
`;

const CardDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CardDetail = styled.span`
  font-size: 13px;
  color: #8890a5;
`;

const LoadMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 0;
`;

const Section = styled.div`
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);

  h3 {
    margin: 0;
    font-size: 18px;
    color: #1a1d2b;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;
  margin-bottom: 0;

  &:hover {
    opacity: 0.8;
  }
`;

const SectionHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SectionStats = styled.div`
  font-size: 14px;
  color: #8890a5;
  font-weight: normal;
`;

const ExpandIcon = styled.div`
  font-size: 12px;
  color: #8890a5;
`;

const SuggestionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

const SuggestionCard = styled.div`
  display: flex;
  gap: 16px;
  padding: 12px 16px;
  border: 1px solid #e4e7ef;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;
  background: #fafafc;

  &:hover {
    background: #f0f1f6;
    border-color: #c7cad6;
  }
`;

const SuggestionCardImage = styled.img`
  width: 72px;
  height: 72px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
`;

const SuggestionCardBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SuggestionCardTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SuggestionTypeBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #5a6178;
  background: #e4e7ef;
  padding: 2px 8px;
  border-radius: 4px;
`;

const SUGGESTION_STATUS_COLORS: Record<string, string> = {
  PENDING: '#ed8936',
  PENDING_REVIEW: '#ed8936',
  SEEN: '#3182ce',
  ACTED_ON: '#38a169',
  DISMISSED: '#a0aec0',
  INVALID: '#e53e3e',
};

const SuggestionStatusBadge = styled.span<{ $status: string }>`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: white;
  background: ${({ $status }) => SUGGESTION_STATUS_COLORS[$status] ?? '#8890a5'};
  padding: 2px 8px;
  border-radius: 4px;
`;

const SuggestionCardTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1a1d2b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SuggestionCardMessage = styled.div`
  font-size: 13px;
  color: #5a6178;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const SuggestionCardMeta = styled.div`
  font-size: 12px;
  color: #8890a5;
  font-weight: 500;
`;

const ErrorMessage = styled.div`
  color: #e53e3e;
  padding: 12px;
  background: #fff5f5;
  border-radius: 6px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px;
  color: #8890a5;
  font-size: 16px;
`;

const ConversationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
`;

const ConversationItem = styled.div`
  border: 1px solid #e4e7ef;
  border-radius: 8px;
  overflow: hidden;
`;

const ConversationHeaderClickable = styled.div`
  padding: 16px;
  background: #f8f9fc;
  border-bottom: 1px solid #e4e7ef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;

  &:hover {
    background: #f0f1f6;
  }
`;

const ConversationHeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ConversationHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ConversationTitle = styled.div`
  font-weight: 600;
  color: #1a1d2b;
  font-size: 14px;
  user-select: text;
  cursor: text;
`;

const ConversationId = styled.div`
  font-size: 12px;
  color: #8890a5;
  font-family: monospace;
  user-select: text;
  cursor: text;
`;

const LoadingMessages = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
`;

const EmptyMessages = styled.div`
  text-align: center;
  padding: 24px;
  color: #8890a5;
  font-size: 14px;
`;

const MessagesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  max-height: 400px;
  overflow-y: auto;
`;

const MessageItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  background: #f8f9fc;
  border-radius: 6px;
`;

const MessageHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const MessageRole = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #5a6178;
  text-transform: uppercase;
`;

const TraceButton = styled.button`
  background: #008aff;
  color: white;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #0070d6;
  }

  &:disabled {
    background: #8890a5;
    cursor: not-allowed;
  }
`;

const MessageContent = styled.div`
  font-size: 14px;
  color: #1a1d2b;

  p {
    margin: 0;
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
`;

const ActionCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 12px;
  background: white;
  border: 1px solid #e4e7ef;
  border-radius: 8px;
`;

const ActionBadge = styled.span<{ $color: string }>`
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: white;
  background: ${({ $color }) => $color};
  margin-top: 1px;
`;

const ActionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const ActionPrimary = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #1a1d2b;
  word-break: break-word;

  &[href] {
    color: #2563eb;
    text-decoration: none;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ActionSecondary = styled.span`
  font-size: 12px;
  color: #6b7280;
  word-break: break-word;
`;

const ModalActionsSection = styled.div`
  padding: 16px 32px 24px;
  border-top: 1px solid #e4e7ef;
`;

const MessageImages = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const MessageImage = styled.img`
  max-width: 200px;
  max-height: 200px;
  border-radius: 8px;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }
`;

const StatsSection = styled.div`
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
`;

const StatsHeader = styled.div`
  margin-bottom: 16px;

  h3 {
    margin: 0;
    font-size: 18px;
    color: #1a1d2b;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatGroup = styled.div``;

const StatGroupTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #5a6178;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StatItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f8f9fc;
  border-radius: 6px;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: #5a6178;
`;

const StatValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1a1d2b;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InteractedBadge = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #38a169;
  background: #c6f6d5;
  padding: 2px 8px;
  border-radius: 10px;
`;

const FailedRequestsSection = styled.div`
  padding: 12px 16px;
  background: #fff5f5;
  border-bottom: 1px solid #feb2b2;
`;

const FailedRequestsHeader = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #c53030;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const FailedRequestItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: white;
  border: 1px solid #feb2b2;
  border-radius: 6px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FailedRequestInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FailedRequestTime = styled.span`
  font-size: 13px;
  color: #5a6178;
`;

const FailedRequestStatus = styled.span<{ $status: number }>`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  text-transform: uppercase;
  background: ${(props) => (props.$status === 3 ? '#fed7d7' : props.$status === 2 ? '#c6f6d5' : '#feebc8')};
  color: ${(props) => (props.$status === 3 ? '#c53030' : props.$status === 2 ? '#38a169' : '#c05621')};
`;

const McpTracesTable = styled.table`
  width: 100%;
  margin-top: 16px;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    padding: 8px 12px;
    color: #5a6178;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-size: 11px;
    border-bottom: 2px solid #e4e7ef;
  }

  td {
    padding: 8px 12px;
    color: #1a1d2b;
    border-bottom: 1px solid #f0f1f6;
    vertical-align: middle;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const OnboardingTable = styled.table`
  width: 100%;
  margin-top: 16px;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    padding: 8px 12px;
    color: #5a6178;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-size: 11px;
    border-bottom: 2px solid #e4e7ef;
  }

  td {
    padding: 8px 12px;
    color: #1a1d2b;
    border-bottom: 1px solid #f0f1f6;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const OverallLatencyCell = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;

  > span:first-child {
    min-width: 56px;
  }
`;

const OverallLatencyHint = styled.span`
  font-weight: 400;
  text-transform: none;
  letter-spacing: normal;
  color: #8890a5;
  white-space: nowrap;
`;

export default UserViewPage;
